const artistWisdomImage = '/artist_wisdom_01 copy.png';
const civicMomentImage = '/civic_moment_01 copy.png';

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
}

interface WeeklyCardBackProps {
  cardType: 'artist_wisdom' | 'civic_moment' | 'civic_spark';
  imageUrl?: string | null;
  content: string;
  attribution?: string | null;
  title?: string | null;
  subtext?: string | null;
  countryName?: string | null;
  countryCode?: string | null;
  eventDate?: string | null;
  sourceName?: string | null;
  sourceUrl?: string | null;
  headline?: string | null;
}

export default function WeeklyCardBack({
  cardType,
  imageUrl,
  content,
  attribution,
  title,
  subtext,
  countryName,
  countryCode,
  eventDate,
  sourceName,
  sourceUrl,
  headline,
}: WeeklyCardBackProps) {
  const getTypeBadgeText = () => {
    if (cardType === 'artist_wisdom') return 'Artist Wisdom';
    if (cardType === 'civic_moment') return 'Civic Moment';
    if (cardType === 'civic_spark') return 'Spark a Thought';
    return '';
  };

  return (
    <div className="flex flex-col h-full px-5 pt-6 pb-6 justify-center">
      {/* 1. TYPE BADGE */}
      <div className="flex justify-center mb-6">
        <div className="inline-flex px-3 py-1.5 rounded-full bg-bg-overlay/90 backdrop-blur-sm border border-stroke">
          <span className="text-[9px] font-bold uppercase tracking-widest text-[#40E0D0]">
            {getTypeBadgeText()}
          </span>
        </div>
      </div>

      {/* 2. IMAGE MODULE */}
      <div className="flex justify-center mb-8">
        {cardType === 'artist_wisdom' && (
          <div className="w-[120px] h-[120px] rounded-full border border-stroke bg-black/20 flex items-center justify-center overflow-hidden">
            <img
              src={imageUrl || artistWisdomImage}
              alt="Artist"
              className="w-full h-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).src = artistWisdomImage;
              }}
            />
          </div>
        )}

        {cardType === 'civic_moment' && (
          <div className="mt-2 mb-2">
            <div className="w-[160px] h-[120px] rounded-[16px] border border-stroke bg-black/20 flex items-center justify-center overflow-hidden">
              <img
                src={imageUrl || civicMomentImage}
                alt="Civic moment"
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = civicMomentImage;
                }}
              />
            </div>
          </div>
        )}

        {cardType === 'civic_spark' && (
          <div className="h-6" />
        )}
      </div>

      {/* 3. MAIN CONTENT BLOCK */}
      <div className="flex-1 flex flex-col justify-start items-center px-4">
        {cardType === 'artist_wisdom' && (
          <>
            <h2 className="text-[22px] font-semibold text-text-primary leading-[1.3] text-center mb-3 max-w-[280px] mx-auto">
              "{content}"
            </h2>
            {attribution && (
              <p className="text-[13px] text-text-secondary text-center">
                {attribution.startsWith('—') || attribution.startsWith('-') ? attribution : `— ${attribution}`}
              </p>
            )}
          </>
        )}

        {cardType === 'civic_moment' && (
          <>
            <h3 className="text-[20px] font-semibold text-text-primary leading-[1.3] text-center mb-3 max-w-[280px] mx-auto">
              {headline || title || countryName || 'Civic Moment'}
            </h3>
            <p className="text-[13px] text-text-secondary text-center leading-[1.5] max-w-[340px] mx-auto mb-4">
              {content}
            </p>
            {(countryCode || eventDate || sourceName) && (
              <div className="text-[11px] text-text-secondary/70 text-center flex items-center justify-center gap-1.5 flex-wrap">
                {countryCode && <span>{countryCode}</span>}
                {countryCode && (eventDate || sourceName) && <span>•</span>}
                {eventDate && <span>{formatDate(eventDate)}</span>}
                {eventDate && sourceName && <span>•</span>}
                {sourceName && sourceUrl ? (
                  <a
                    href={sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-[#40E0D0] transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Source: {sourceName}
                  </a>
                ) : sourceName ? (
                  <span>Source: {sourceName}</span>
                ) : null}
              </div>
            )}
          </>
        )}

        {cardType === 'civic_spark' && (
          <>
            <h2 className="text-[22px] font-semibold text-text-primary leading-[1.3] text-center max-w-[280px] mx-auto">
              {content}
            </h2>
            {subtext && (
              <p className="text-[13px] text-text-secondary text-center mt-40">
                {subtext}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
